import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AmplitudeEventType } from '../../../../../analytics/amplitudeEvents';
import { ClientConfig } from '../../../../../clientConfig';
import { PimsPostVerificationSessionRequest } from '../../../../../router/types';
import { ClientConfigContext } from '../../../../clientConfigContext';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { useBrand } from '../../../../hooks/useBrand';
import { useQueryParams } from '../../../../hooks/useQueryParams';
import { postIdentityVerificationSession } from '../../../../services/pims/pimsClient';
import { getDeviceCategory } from '../../../../util/clientContextUtils';
import Button from '../../../elements/Button';
import { AnalyticsIdentityVerificationMethod } from '../analytics';
import BasicButton from '../identityVerificationPage/components/BasicButton';
import { ClearIcon } from './ClearIcon';
import { VENDOR_NAME as CLEAR_VENDOR_NAME } from './index';
import { ClearConfig } from './types';

type ClearQueryParams = {
    ehr_system: string;
    client_id: string;
    sdk_version: string;
    redirect_uri: string;
    redirect_url?: string;
    mobile?: string;
    custom_redirect_scheme?: string; // Used by mobile to deep-link after Clear Verification
};

type ClearButtonProps = {
    key: number;
    width: number;
    jwt: string;
    onError: (error: Error) => void;
    onSessionCreated: (verificationSessionId: string) => void;
    onClick?: () => void;
    token?: string;
};

// CLEAR styling UX requirements. See: https://docs.clearme.com/docs/ux-guidelines
const getTheme = (isDarkTheme: boolean) => ({
    backgroundColor: isDarkTheme ? '#041A55' : '#ffffff',
    hoverBackgroundColor: '#394168',
    fontFamily: 'Inter',
});

const getLogoIconUri = (isDarkTheme: boolean, cdnPath: string, config: ClearConfig): string => {
    return `${cdnPath}${isDarkTheme ? config.whiteLogoIconUri : config.blackLogoIconUri}`;
};

/**
 * A button to navigate to the CLEAR Hosted UI for identity verification.
 * @param config - The configuration for the CLEAR vendor.
 * @param width - The width of the button (in pixels).
 * @param token - A CLEAR session token. If not provided, we will create a new session first, before navigating to the CLEAR Hosted UI.
 * @param onError - A callback function to execute when an error occurs.
 * @param onSessionCreated - A callback function to execute when a new session is created.
 */
export const ClearButton: React.FC<ClearButtonProps> = ({ key, token, width, jwt, onError, onSessionCreated }: ClearButtonProps) => {
    const text = token ? 'Continue with CLEAR' : 'Verify with CLEAR';

    const { brand, identityVerification: brandIdentityVerification } = useBrand();
    const { cdnPath, pimsApiUrl, identityVerification } = useContext<ClientConfig>(ClientConfigContext);
    const { i18n } = useTranslation();
    const { sendEvent } = useAnalytics({ eventSource: '', useJWT: true });
    const { queryParams } = useQueryParams<ClearQueryParams>({
        requiredParams: ['sdk_version', 'client_id'],
    });

    const clearOptions = identityVerification.clear!;

    const useCreateClearVerificationSessionFlow = async (): Promise<string> => {
        const ehrSystem = queryParams?.ehr_system as string;
        const redirectUriParam = queryParams?.redirect_uri;
        const redirectUrlParam = queryParams?.redirect_url;
        const redirectUri = (redirectUriParam || redirectUrlParam) as string;

        // SPI-1816, for mobile, we need to load Clear in a secure webview.
        // It will pass a customScheme to the API that will modify the Clear redirect_uri.
        // Conditions: `mobile` is true
        const mobileSwitch: boolean = queryParams?.mobile === 'true';
        const customScheme = mobileSwitch ? (queryParams?.custom_redirect_scheme as string) : undefined;

        try {
            const postVerificationSessionParams: PimsPostVerificationSessionRequest = {
                jwt,
                pimsApiUrl,
                brand,
                ehrSystem,
                deviceType: getDeviceCategory(),
                redirectUri,
                customScheme,
                vendor: CLEAR_VENDOR_NAME,
                language: i18n.language,
            };
            const response = await postIdentityVerificationSession(postVerificationSessionParams);

            if (!response || !response.sessionTokenId || !response.verificationSessionId) {
                throw new Error(`Failed to create verification session: ${JSON.stringify(response)}`);
            }

            onSessionCreated(response.verificationSessionId);
            return response.sessionTokenId;
        } catch (error) {
            onError(error as Error);
            return '';
        }
    };

    /**
     * If no token exists already, create a new session and use the new token.
     * If session creation fails, no-op. The parent component should handle the error via the onError callback.
     */
    const handleClick = async () => {
        sendEvent(AmplitudeEventType.IdentityVerification_MethodSelection_Select, {
            activation_method: AnalyticsIdentityVerificationMethod.CLEAR,
        });
        if (!token) {
            token = await useCreateClearVerificationSessionFlow();
        }
        return window.location.replace(`${clearOptions.verifyUrl}?token=${token}`);
    };
    const theme = brandIdentityVerification.theme;
    const isDarkTheme = theme === 'blue';
    const logoIconUri = getLogoIconUri(isDarkTheme, cdnPath, clearOptions);
    const chosenTheme = getTheme(isDarkTheme);

    return isDarkTheme ? (
        <Button
            key={key}
            variant='contained'
            activeBackgroundColor={chosenTheme.hoverBackgroundColor}
            backgroundColor={chosenTheme.backgroundColor}
            fontFamily={chosenTheme.fontFamily}
            hoverBackgroundColor={chosenTheme.hoverBackgroundColor}
            paddingLeft={0}
            paddingRight={0}
            startIcon={<ClearIcon logoIconUri={logoIconUri} />}
            textTransform='none'
            width={width}
            onClick={handleClick}
            overrideWidth={'auto'}
        >
            {text}
        </Button>
    ) : (
        <BasicButton key={key} onClick={handleClick} icon={<ClearIcon logoIconUri={logoIconUri} />}>
            {text}
        </BasicButton>
    );
};
